import * as React from "react"
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  Loader2,
  Search,
  Send,
  Star,
  Users,
  UserPlus,
  X,
} from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import { AdminCard } from "./AdminCard"

type AirdropTarget = "all" | "specific"
type AirdropCurrency = "cash" | "points"
type Feedback = { type: "success" | "error"; text: string } | null

const DEADLINE_PRESETS: Array<{ label: string; hours: number | null }> = [
  { label: "No deadline", hours: null },
  { label: "24 hours", hours: 24 },
  { label: "48 hours", hours: 48 },
  { label: "72 hours", hours: 72 },
  { label: "7 days", hours: 168 },
  { label: "30 days", hours: 720 },
]

interface AirdropHistoryEntry {
  amount: number
  currency: AirdropCurrency
  target: AirdropTarget
  usersAffected: number
  claimDeadlineHours: number | null
  note: string | null
  timestamp: Date
}

export function AdminAirdrop() {
  const { adminAirdropBalance, searchAdminUsers } = useAuth()

  const [target, setTarget] = React.useState<AirdropTarget>("all")
  const [currency, setCurrency] = React.useState<AirdropCurrency>("cash")
  const [amount, setAmount] = React.useState("")
  const [note, setNote] = React.useState("")
  const [claimDeadlineHours, setClaimDeadlineHours] = React.useState<number | null>(null)
  const [customDeadline, setCustomDeadline] = React.useState("")
  const [sending, setSending] = React.useState(false)
  const [feedback, setFeedback] = React.useState<Feedback>(null)
  const [showConfirm, setShowConfirm] = React.useState(false)

  // Specific users
  const [searchQuery, setSearchQuery] = React.useState("")
  const [searchResults, setSearchResults] = React.useState<Array<{ _id: string; email: string; displayName: string }>>([])
  const [searching, setSearching] = React.useState(false)
  const [selectedUsers, setSelectedUsers] = React.useState<Array<{ _id: string; email: string; displayName: string }>>([])

  // History (session-only)
  const [history, setHistory] = React.useState<AirdropHistoryEntry[]>([])

  const searchTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearch = React.useCallback(
    (query: string) => {
      setSearchQuery(query)
      if (searchTimeout.current) clearTimeout(searchTimeout.current)
      if (query.trim().length < 2) {
        setSearchResults([])
        return
      }
      searchTimeout.current = setTimeout(async () => {
        setSearching(true)
        const result = await searchAdminUsers(query.trim())
        if (result.success && result.users) {
          setSearchResults(
            result.users.map((u) => ({ _id: u._id, email: u.email, displayName: u.displayName }))
          )
        }
        setSearching(false)
      }, 300)
    },
    [searchAdminUsers]
  )

  const addUser = (user: { _id: string; email: string; displayName: string }) => {
    if (!selectedUsers.find((u) => u._id === user._id)) {
      setSelectedUsers((prev) => [...prev, user])
    }
    setSearchQuery("")
    setSearchResults([])
  }

  const removeUser = (id: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u._id !== id))
  }

  const parsedAmount = parseFloat(amount)
  const isValidAmount = Number.isFinite(parsedAmount) && parsedAmount > 0
  const canSubmit =
    isValidAmount &&
    !sending &&
    (target === "all" || selectedUsers.length > 0)

  const effectiveDeadline = claimDeadlineHours !== null
    ? claimDeadlineHours
    : customDeadline
      ? parseFloat(customDeadline)
      : null
  const isCustomDeadlineMode = claimDeadlineHours === null && DEADLINE_PRESETS.every((p) => p.hours !== null) === false

  const handleSubmit = () => {
    if (!canSubmit) return
    setShowConfirm(true)
  }

  const formatAmountLabel = (amt: number, cur: AirdropCurrency) =>
    cur === "cash" ? `$${amt.toFixed(2)}` : `${amt.toLocaleString()} pts`

  const formatDeadlineLabel = (hours: number | null) => {
    if (hours === null) return "No deadline (instant)"
    if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""}`
    const days = hours / 24
    if (Number.isInteger(days)) return `${days} day${days !== 1 ? "s" : ""}`
    return `${hours} hours`
  }

  const executeAirdrop = async () => {
    setShowConfirm(false)
    setSending(true)
    setFeedback(null)

    const result = await adminAirdropBalance({
      amount: parsedAmount,
      currency,
      target,
      emails: target === "specific" ? selectedUsers.map((u) => u.email) : undefined,
      claimDeadlineHours: effectiveDeadline && effectiveDeadline > 0 ? effectiveDeadline : undefined,
      note: note.trim() || undefined,
    })

    if (result.success && result.airdrop) {
      setFeedback({ type: "success", text: result.message || "Airdrop sent!" })
      setHistory((prev) => [
        {
          amount: result.airdrop!.amount,
          currency: result.airdrop!.currency,
          target: result.airdrop!.target,
          usersAffected: result.airdrop!.usersAffected,
          claimDeadlineHours: result.airdrop!.claimDeadlineHours,
          note: result.airdrop!.note,
          timestamp: new Date(),
        },
        ...prev,
      ])
      setAmount("")
      setNote("")
      setCustomDeadline("")
      if (target === "specific") setSelectedUsers([])
    } else {
      setFeedback({ type: "error", text: result.message || "Airdrop failed" })
    }

    setSending(false)
  }

  const isCash = currency === "cash"
  const accentColor = isCash ? "#F37020" : "#FFD700"

  return (
    <div className="space-y-6">
      {/* Main Airdrop Card */}
      <AdminCard>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}15` }}>
            <Send className="w-4.5 h-4.5" style={{ color: accentColor }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white/80 font-admin">Send Airdrop</h3>
            <p className="text-[11px] text-white/30 font-admin">Distribute cash or points to user accounts</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Currency Toggle */}
          <div className="space-y-1.5">
            <span className="text-xs text-white/40 font-admin">Currency</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCurrency("cash")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-admin transition-all ${
                  isCash
                    ? "bg-[#F37020]/10 border-[#F37020]/40 text-[#F37020]"
                    : "bg-[#0C0C12] border-[#2A2A45] text-white/40 hover:border-white/20 hover:text-white/60"
                }`}
              >
                <DollarSign className="w-4 h-4" />
                Cash (Pending Balance)
              </button>
              <button
                type="button"
                onClick={() => setCurrency("points")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-admin transition-all ${
                  !isCash
                    ? "bg-[#FFD700]/10 border-[#FFD700]/40 text-[#FFD700]"
                    : "bg-[#0C0C12] border-[#2A2A45] text-white/40 hover:border-white/20 hover:text-white/60"
                }`}
              >
                <Star className="w-4 h-4" />
                Points
              </button>
            </div>
          </div>

          {/* Target Selection */}
          <div className="space-y-1.5">
            <span className="text-xs text-white/40 font-admin">Recipients</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setTarget("all")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-admin transition-all ${
                  target === "all"
                    ? "bg-white/[0.06] border-white/20 text-white"
                    : "bg-[#0C0C12] border-[#2A2A45] text-white/40 hover:border-white/20 hover:text-white/60"
                }`}
              >
                <Users className="w-4 h-4" />
                All Users
              </button>
              <button
                type="button"
                onClick={() => setTarget("specific")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-admin transition-all ${
                  target === "specific"
                    ? "bg-[#9B59B6]/10 border-[#9B59B6]/40 text-[#9B59B6]"
                    : "bg-[#0C0C12] border-[#2A2A45] text-white/40 hover:border-white/20 hover:text-white/60"
                }`}
              >
                <UserPlus className="w-4 h-4" />
                Specific Users
              </button>
            </div>
          </div>

          {/* User Search (when specific) */}
          {target === "specific" && (
            <div className="space-y-2">
              <span className="text-xs text-white/40 font-admin">Search Users</span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
                <input
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-[#9B59B6]/40 transition-colors font-admin"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 animate-spin" />
                )}
              </div>

              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div className="rounded-lg border border-[#2A2A45] bg-[#0C0C12] max-h-40 overflow-y-auto">
                  {searchResults.map((user) => {
                    const alreadyAdded = selectedUsers.some((u) => u._id === user._id)
                    return (
                      <button
                        key={user._id}
                        type="button"
                        onClick={() => !alreadyAdded && addUser(user)}
                        disabled={alreadyAdded}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between text-sm border-b border-[#2A2A45] last:border-b-0 transition-colors font-admin ${
                          alreadyAdded
                            ? "text-white/20 cursor-default"
                            : "text-white/60 hover:bg-white/[0.03] hover:text-white/80 cursor-pointer"
                        }`}
                      >
                        <div>
                          <span className="text-white/70">{user.displayName}</span>
                          <span className="text-white/30 ml-2">{user.email}</span>
                        </div>
                        {alreadyAdded && <span className="text-[10px] text-white/20">Added</span>}
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Selected Users Tags */}
              {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedUsers.map((user) => (
                    <span
                      key={user._id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#9B59B6]/10 border border-[#9B59B6]/20 text-[11px] text-[#9B59B6] font-admin"
                    >
                      {user.displayName || user.email}
                      <button
                        type="button"
                        onClick={() => removeUser(user._id)}
                        className="hover:text-red-400 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSelectedUsers([])}
                    className="text-[10px] text-white/30 hover:text-red-400 transition-colors font-admin px-2 py-1"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Amount */}
          <div className="space-y-1.5">
            <span className="text-xs text-white/40 font-admin">
              Amount {isCash ? "(USD)" : "(Points)"}
            </span>
            <div className="relative">
              {isCash ? (
                <DollarSign className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
              ) : (
                <Star className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
              )}
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={isCash ? "0.00" : "0"}
                min={isCash ? "0.01" : "1"}
                step={isCash ? "0.01" : "1"}
                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none transition-colors font-admin [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                style={{ borderColor: undefined }}
                onFocus={(e) => e.currentTarget.style.borderColor = `${accentColor}66`}
                onBlur={(e) => e.currentTarget.style.borderColor = ""}
              />
            </div>
            {isValidAmount && (
              <p className="text-[10px] text-white/25 font-admin">
                Each {target === "all" ? "onboarded user" : "selected user"} will receive{" "}
                {formatAmountLabel(parsedAmount, currency)}
                {isCash ? " in pending balance" : " added to their points"}
              </p>
            )}
          </div>

          {/* Claim Deadline */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-white/30" />
              <span className="text-xs text-white/40 font-admin">Claim Deadline</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {DEADLINE_PRESETS.map((preset) => {
                const isActive = claimDeadlineHours === preset.hours && !customDeadline
                return (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => {
                      setClaimDeadlineHours(preset.hours)
                      setCustomDeadline("")
                    }}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-admin transition-all ${
                      isActive
                        ? "bg-white/[0.06] border-white/20 text-white"
                        : "bg-[#0C0C12] border-[#2A2A45] text-white/40 hover:border-white/20 hover:text-white/60"
                    }`}
                  >
                    {preset.label}
                  </button>
                )
              })}
              {/* Custom input */}
              <div className="relative">
                <input
                  type="number"
                  value={customDeadline}
                  onChange={(e) => {
                    setCustomDeadline(e.target.value)
                    setClaimDeadlineHours(null)
                  }}
                  placeholder="Custom hrs"
                  min="1"
                  className={`w-28 px-3 py-1.5 rounded-lg border text-xs font-admin outline-none transition-all [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                    customDeadline
                      ? "bg-white/[0.06] border-white/20 text-white"
                      : "bg-[#0C0C12] border-[#2A2A45] text-white/40 placeholder:text-white/20"
                  }`}
                />
              </div>
            </div>
            <p className="text-[10px] text-white/20 font-admin">
              {effectiveDeadline
                ? `Users must claim within ${formatDeadlineLabel(effectiveDeadline)}. Unclaimed airdrops expire automatically.`
                : "No deadline — balance/points applied instantly."}
            </p>
          </div>

          {/* Note */}
          <div className="space-y-1.5">
            <span className="text-xs text-white/40 font-admin">Note (optional)</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason for airdrop..."
              maxLength={300}
              className="w-full px-3 py-2.5 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-white/20 transition-colors font-admin"
            />
          </div>

          {/* Feedback */}
          {feedback && (
            <div
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-admin ${
                feedback.type === "success"
                  ? "bg-green-500/10 border border-green-500/20 text-green-400"
                  : "bg-red-500/10 border border-red-500/20 text-red-400"
              }`}
            >
              {feedback.type === "success" ? (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              )}
              {feedback.text}
            </div>
          )}

          {/* Submit */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-white text-sm font-semibold font-admin hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            style={{ backgroundColor: accentColor }}
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {sending
              ? "Sending..."
              : `Send ${currency === "cash" ? "Cash" : "Points"} Airdrop${
                  target === "specific" && selectedUsers.length > 0
                    ? ` to ${selectedUsers.length} user${selectedUsers.length > 1 ? "s" : ""}`
                    : target === "all"
                      ? " to All Users"
                      : ""
                }`}
          </button>
        </div>
      </AdminCard>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowConfirm(false)} />
          <div className="relative w-full max-w-md bg-[#111117] rounded-2xl border border-[#2A2A45] shadow-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accentColor}15` }}>
                <AlertTriangle className="w-5 h-5" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white font-admin">Confirm Airdrop</h3>
                <p className="text-[11px] text-white/30 font-admin">This action cannot be undone</p>
              </div>
            </div>

            <div className="space-y-2 px-1">
              <div className="flex justify-between text-sm font-admin">
                <span className="text-white/40">Type</span>
                <span className="font-semibold" style={{ color: accentColor }}>
                  {isCash ? "Cash (Pending Balance)" : "Points"}
                </span>
              </div>
              <div className="flex justify-between text-sm font-admin">
                <span className="text-white/40">Amount per user</span>
                <span className="text-white font-semibold">{formatAmountLabel(parsedAmount, currency)}</span>
              </div>
              <div className="flex justify-between text-sm font-admin">
                <span className="text-white/40">Recipients</span>
                <span className="text-white font-semibold">
                  {target === "all" ? "All onboarded users" : `${selectedUsers.length} selected user${selectedUsers.length > 1 ? "s" : ""}`}
                </span>
              </div>
              <div className="flex justify-between text-sm font-admin">
                <span className="text-white/40">Claim deadline</span>
                <span className="text-white font-semibold">
                  {formatDeadlineLabel(effectiveDeadline)}
                </span>
              </div>
              {note.trim() && (
                <div className="flex justify-between text-sm font-admin">
                  <span className="text-white/40">Note</span>
                  <span className="text-white/60 text-right max-w-[60%] truncate">{note.trim()}</span>
                </div>
              )}
            </div>

            {effectiveDeadline && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#FFD700]/5 border border-[#FFD700]/10">
                <Clock className="w-3.5 h-3.5 text-[#FFD700]/50 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-[#FFD700]/60 font-admin">
                  Users will have {formatDeadlineLabel(effectiveDeadline)} to claim. Unclaimed airdrops will expire and the {isCash ? "balance" : "points"} will not be applied.
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-[#1C1C32] border border-[#2A2A45] text-sm text-white/60 font-admin hover:text-white/80 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={executeAirdrop}
                className="flex-1 px-4 py-2.5 rounded-lg text-white text-sm font-semibold font-admin hover:opacity-90 transition-colors"
                style={{ backgroundColor: accentColor }}
              >
                Confirm & Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session History */}
      {history.length > 0 && (
        <AdminCard delay={0.1}>
          <h3 className="text-sm font-semibold text-white/80 font-admin mb-3">Recent Airdrops (this session)</h3>
          <div className="space-y-2">
            {history.map((entry, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-[#0C0C12] border border-[#1E1E2A] text-sm font-admin"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <div>
                    <span
                      className="font-medium"
                      style={{ color: entry.currency === "cash" ? "#F37020" : "#FFD700" }}
                    >
                      {formatAmountLabel(entry.amount, entry.currency)}
                    </span>
                    <span className="text-white/30 mx-1.5">&rarr;</span>
                    <span className="text-white/50">
                      {entry.target === "all" ? "All users" : `${entry.usersAffected} user${entry.usersAffected !== 1 ? "s" : ""}`}
                    </span>
                    {entry.claimDeadlineHours && (
                      <span className="text-white/25 ml-2 text-[10px]">
                        ({formatDeadlineLabel(entry.claimDeadlineHours)} to claim)
                      </span>
                    )}
                    {entry.note && (
                      <span className="text-white/25 ml-2 text-[11px]">"{entry.note}"</span>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-white/20 flex-shrink-0">
                  {entry.timestamp.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        </AdminCard>
      )}
    </div>
  )
}
